/**
 * useDealModals
 * 
 * Hook para gerenciar modais de ganho e perda de negociações.
 * Integra com use cases para atualizar status dos leads.
 * 
 * Princípios aplicados:
 * - Use Case Pattern: Delega lógica de negócio a use cases
 * - Single Responsibility: Apenas gestão de modais de deal
 */

import { useState, useCallback } from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useLeads } from '../../../contexts/leads/LeadsContext';
import { notify } from '../../../utils/toast';
import { Lead } from '../../../types';
import { createModuleLogger } from '../../../utils/logger';

// Centralized DI
import { winDealUseCase, loseDealUseCase } from '../../../infrastructure/di/crmContainer';

const log = createModuleLogger('[DealModals]');

interface LossReasonModalState {
    isOpen: boolean;
    leadId: string;
    leadName: string;
}

interface WinDealModalState {
    isOpen: boolean;
    leadId: string;
    leadName: string;
    currentValue: number;
}

export const useDealModals = (leadsByStage: { [key: string]: Lead[] }) => {
    const { refreshLeads } = useLeads();
    const { currentOrganization } = useOrganization();

    const [lossReasonModal, setLossReasonModal] = useState<LossReasonModalState>({
        isOpen: false, leadId: '', leadName: ''
    });

    const [winDealModal, setWinDealModal] = useState<WinDealModalState>({
        isOpen: false, leadId: '', leadName: '', currentValue: 0
    });

    const handleFinalStageDrop = async (leadId: string, stageId: 'won' | 'lost') => {
        const lead = Object.values(leadsByStage).flat().find(l => l.id === leadId);

        if (!lead) {
            notify.error('Negociação não encontrada');
            return;
        }

        // Validação preventiva: contato obrigatório para ganho
        if (stageId === 'won' && !lead.contactId) {
            notify.error('Vincule um contato à negociação antes de marcá-la como ganha.');
            return;
        }

        // Permite mover entre won/lost ou abrir modais
        if (stageId === 'lost') {
            setLossReasonModal({ isOpen: true, leadId, leadName: lead.name });
        } else {
            setWinDealModal({
                isOpen: true, leadId, leadName: lead.name, currentValue: lead.value || 0
            });
        }
    };

    const handleConfirmWin = async (finalValue: number) => {
        if (!currentOrganization) {
            notify.error('Organização não encontrada');
            return;
        }

        try {
            await winDealUseCase.execute({
                leadId: winDealModal.leadId,
                organizationId: currentOrganization.id,
                finalValue
            });

            notify.success('Negociação marcada como ganha! 🎉');
            setWinDealModal({ isOpen: false, leadId: '', leadName: '', currentValue: 0 });
            await refreshLeads();
        } catch (error: any) {
            log.error('Erro ao marcar como ganho:', error);
            notify.error(error.message || 'Erro ao marcar negociação como ganha');
        }
    };

    const handleConfirmLoss = async (reasonId: string, comments?: string) => {
        if (!currentOrganization) {
            notify.error('Organização não encontrada');
            return;
        }

        try {
            await loseDealUseCase.execute({
                leadId: lossReasonModal.leadId,
                organizationId: currentOrganization.id,
                lostReasonId: reasonId,
                comments
            });

            setLossReasonModal({ isOpen: false, leadId: '', leadName: '' });
            await refreshLeads();
        } catch (error: any) {
            log.error('Erro ao marcar como perda:', error);
            notify.error(error.message || 'Erro ao marcar negociação como perdida');
        }
    };

    return {
        lossReasonModal,
        winDealModal,
        closeLossModal: () => setLossReasonModal({ isOpen: false, leadId: '', leadName: '' }),
        closeWinModal: () => setWinDealModal({ isOpen: false, leadId: '', leadName: '', currentValue: 0 }),
        handleFinalStageDrop,
        handleConfirmWin,
        handleConfirmLoss
    };
};
